import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { User, UserLogin } from '@types';
import { login as apiLogin, getCurrentUser, logout as apiLogout } from '@/services/api/authService';
import { getToken, saveUser, clearStorage } from '@/utils/storage';

type AuthContextType = {
  user: User | null;
  loading: boolean;
  login: (credentials: UserLogin) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isTenantAdmin: boolean;
  isTenantUser: boolean;
  isPartnerUser: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // 初回ロード時にトークンがあれば自動ログイン
  useEffect(() => {
    const initAuth = async () => {
      const token = getToken();
      if (token) {
        try {
          const userData = await getCurrentUser();
          setUser(userData);
          saveUser(userData);
        } catch (error) {
          console.error('認証エラー:', error);
          clearStorage();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials: UserLogin) => {
    await apiLogin(credentials);
    const userData = await getCurrentUser();
    setUser(userData);
    saveUser(userData);
  };

  const logout = () => {
    apiLogout();
    setUser(null);
    clearStorage();
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role?.toUpperCase() === 'ADMIN',
    isTenantAdmin: user?.role?.toUpperCase() === 'TENANT_ADMIN',
    isTenantUser: !!user?.tenant_id,
    isPartnerUser: !!user?.tenant_id // パートナーはテナントユーザーとして扱う（バックエンドでOEMプランを検証）
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
